import { ReactNode } from "react";
import { PivotTab } from "../../types/pivot.type";
import { PIVOT_NEW_TABS, PIVOT_TAB_LABEL } from "./pivotFormat";
import { PV, cardSx } from "./pivotTokens";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";

const PIVOT_TABS = Object.keys(PIVOT_TAB_LABEL) as PivotTab[];

/** Segmented tab strip (mockup `.segs`) — one card, equal-width buttons, tinted active. */
export function PivotTabs({
  tab,
  onChange,
}: {
  tab: PivotTab;
  onChange: (t: PivotTab) => void;
}) {
  return (
    <Box sx={{ ...cardSx, display: "flex", gap: 1, flexWrap: "wrap", p: 0.75, mb: 1.75 }}>
      {PIVOT_TABS.map((k) => {
        const active = tab === k;
        return (
          <Box
            key={k}
            component="button"
            type="button"
            onClick={() => onChange(k)}
            aria-current={active ? "true" : undefined}
            sx={{
              flex: "1 1 180px",
              minWidth: 180,
              textAlign: "left",
              border: "1px solid",
              borderColor: active ? PV.indigoBorder : "transparent",
              bgcolor: active ? PV.indigoTint : "transparent",
              color: active ? PV.indigo : PV.tx2,
              borderRadius: "10px",
              px: 1.75,
              py: 1.25,
              cursor: "pointer",
              font: "inherit",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              transition: "background .12s",
              "&:hover": { bgcolor: active ? PV.indigoTint : PV.line2 },
            }}
          >
            {PIVOT_TAB_LABEL[k]}
            {PIVOT_NEW_TABS.includes(k) && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  px: 0.9,
                  py: "1px",
                  borderRadius: "999px",
                  verticalAlign: "middle",
                  bgcolor: active ? PV.card : PV.indigoTint,
                  color: PV.indigo,
                }}
              >
                ใหม่
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** Plain-bordered button matching the mockup's `.btn`. */
export function PivotBarButton({
  children,
  onClick,
  disabled,
  primary,
  startIcon,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
  startIcon?: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        font: "inherit",
        fontSize: 13,
        fontWeight: 500,
        cursor: disabled ? "default" : "pointer",
        border: "1px solid",
        borderColor: primary ? PV.indigo : PV.line,
        bgcolor: primary ? PV.indigo : PV.card,
        color: primary ? "#fff" : PV.tx2,
        borderRadius: PV.radiusSm,
        px: 1.9,
        py: 0.9,
        opacity: disabled ? 0.55 : 1,
        whiteSpace: "nowrap",
        "&:hover": disabled
          ? undefined
          : { borderColor: primary ? PV.indigoDark : PV.indigoBorder, bgcolor: primary ? PV.indigoDark : PV.card },
      }}
    >
      {startIcon}
      {children}
    </Box>
  );
}

interface PivotViewBarProps {
  /** Left slot — the view label, or a tab-specific control (store picker / group-by). */
  left: ReactNode;
  searchPlaceholder: string;
  q: string;
  onSearch: (q: string) => void;
  /** Omitted on the flat (ungrouped) product view, where there is nothing to fold. */
  onToggleFoldAll?: () => void;
  allFolded?: boolean;
  onReset: () => void;
}

/** Second toolbar row (mockup `.pvbar`) — view context left, search + actions pushed right. */
export function PivotViewBar({
  left,
  searchPlaceholder,
  q,
  onSearch,
  onToggleFoldAll,
  allFolded,
  onReset,
}: PivotViewBarProps) {
  return (
    <Box
      sx={{
        ...cardSx,
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        flexWrap: "wrap",
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      {left}
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <InputBase
          value={q}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          sx={{
            fontSize: 13.5,
            minWidth: 240,
            bgcolor: PV.card,
            border: "1px solid",
            borderColor: PV.line,
            borderRadius: PV.radiusSm,
            px: 1.5,
            py: 0.9,
            "&:focus-within": { borderColor: PV.indigo },
            "& input::placeholder": { color: PV.tx3, opacity: 1 },
          }}
        />
        {onToggleFoldAll && (
          <PivotBarButton onClick={onToggleFoldAll}>
            {allFolded ? "ขยายทุกกลุ่ม" : "ย่อทุกกลุ่ม"}
          </PivotBarButton>
        )}
        <PivotBarButton onClick={onReset}>ล้างตัวเลือก</PivotBarButton>
      </Box>
    </Box>
  );
}

/** "มุมมอง <current view>" label shown on the three store tabs. */
export function PivotViewLabel({ tab }: { tab: PivotTab }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: PV.tx2 }}>มุมมอง</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: PV.indigo }}>
        {PIVOT_TAB_LABEL[tab]}
      </Typography>
    </Box>
  );
}
